import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { forkJoin, Observable, of, Subject, take, takeUntil } from "rxjs";
import { AlertService } from "src/app/components/alert/alert.service";
import { PolicyService } from "src/app/modules/policy/policy.service";
import { EFormType, ERadioValue, IFormKey } from "src/app/modules/shared/models/forms";
import { IFilledQuestionnaire, IQuestion, IQuestionnaire } from "src/app/modules/shared/models/operation.model";
import { DATE_FORMAT_FROM_BE, ESubjectType, IBeneficiaryObject, ICityObject, ICountriesObject, IProvinceObject } from "src/app/modules/shared/models/policy.model";
import { LoggerService } from "src/app/services/logger.service";
import { SpinnerService } from "src/app/services/spinner.service";
import { AthoraPayloadUtils } from "src/app/utils/AthoraPayloadUtils";
import { CommonUtils } from "src/app/utils/CommonUtils";
import { FormUtils } from "src/app/utils/FormUtils";
import { AthoraService } from "../../athora.service";
import { BeneficiaryFormComponent } from "../beneficiary-form/beneficiary-form.component";
import { EBeneficiaryType, ERelationshipWithSubscriberDesc, ESpecificBeneficiaryType, subject_PF_fields, subject_PG_fields } from "../beneficiary-form/beneficiary-form.constants";
import { SIMOR_subject_PF_fields, SIMOR_subject_PG_fields } from "../claim-notification/claim-notification.constants";
import { CODICE_NAZIONE_ITALIA } from "../contractor-variation/contractor-variation.constants";
import { QuestionnaireModalComponent } from "../questionnaire-modal/questionnaire-modal.component";
import moment = require("moment");

@Component({
  selector: 'app-beneficiary-change',
  templateUrl: './beneficiary-change.component.html',
  styleUrl: './beneficiary-change.component.scss'
})
export class BeneficiaryChangeComponent implements OnInit, OnDestroy {

  @Input() form: FormGroup;
  @Input() beneficiariesList: IBeneficiaryObject[] = [];
  @Input() isReadOnly: boolean;
  @Input() isSimor: boolean;
  @Input() questionnaireForm: FormGroup;
  @Input() questionnaires: IQuestionnaire[] = null;
  @Input() questionnairesArray: IFilledQuestionnaire[];
  @Input() newLiquidationSubjects: Record<
    string,
    {
      rawFormOutcome: Record<string, any>;
      mappedOutcome: Record<string, any>;
    }
  >;
  @Output() viewSubject: EventEmitter<IFormKey> = new EventEmitter<IFormKey>();
  @Output() deleteSubject: EventEmitter<IFormKey> = new EventEmitter<IFormKey>();
  @Output() newLiquidationSubjectsChange = new EventEmitter<Record<string, { rawFormOutcome: Record<string, any>; mappedOutcome: Record<string, any> }>>();

  countriesList: ICountriesObject[];
  birthProvinces: IProvinceObject[];
  birthCities: ICityObject[];
  residenceProvinces: IProvinceObject[];
  residenceCities: ICityObject[];
  subjectPFFields: string[];
  subjectPGFields: string[];
  destroy$ = new Subject();

  constructor (
    private readonly _dialog: MatDialog,
    private readonly _athoraService: AthoraService,
    private readonly _spinnerService: SpinnerService,
    private readonly _alertService: AlertService,
    private readonly _policyService: PolicyService
  ) { }

  ngOnInit(): void {
    this.subjectPFFields = this.isSimor ? CommonUtils.assign(SIMOR_subject_PF_fields) : CommonUtils.assign(subject_PF_fields);
    this.subjectPGFields = this.isSimor ? CommonUtils.assign(SIMOR_subject_PG_fields) : CommonUtils.assign(subject_PG_fields);
    this.retrieveCountries();
  }

  viewBeneficiary(beneficiary: IBeneficiaryObject): void {
    if (beneficiary) {
      this._spinnerService.show();
      let birthProvincesList$: Observable<IProvinceObject[] | null> = of(null);
      let birthCitiesList$: Observable<ICityObject[] | null> = of(null);
      if (beneficiary.birthCountry === CODICE_NAZIONE_ITALIA) {
        birthProvincesList$ = this._athoraService.retrieveProvinces();
        birthCitiesList$ = this._policyService.retrieveCities(beneficiary.birthProvince);
      }
      let residenceProvincesList$: Observable<IProvinceObject[] | null> = of(null);
      let residenceCitiesList$: Observable<ICityObject[] | null> = of(null);
      if (beneficiary.residenceCountry === CODICE_NAZIONE_ITALIA) {
        residenceProvincesList$ = this._athoraService.retrieveProvinces();
        residenceCitiesList$ = this._policyService.retrieveCities(beneficiary.residenceProvince);
      }
      forkJoin([
        birthProvincesList$,
        birthCitiesList$,
        residenceProvincesList$,
        residenceCitiesList$,
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response) {
              this.birthProvinces = response[0];
              this.birthCities = response[1];
              this.residenceProvinces = response[2];
              this.residenceCities = response[3];
              this.openNewBeneficiaryModal(beneficiary);
            }
            this._spinnerService.hide();
          }
        })
    }
  }

  openNewBeneficiaryModal(beneficiary?: IBeneficiaryObject): void {
    this._dialog.open(BeneficiaryFormComponent, {
      data: {
        subjectPFFields: this.subjectPFFields,
        subjectPGFields: this.subjectPGFields,
        isSimor: this.isSimor,
        beneficiary: beneficiary || null,
        countriesList: this.countriesList,
        birthCitiesList: this.birthCities,
        birthProvincesList: this.birthProvinces,
        residenceProvincesList: this.residenceProvinces,
        residenceCitiesList: this.residenceCities,
        isReadOnly: this.isReadOnly
      },
      minWidth: '70vw',
      minHeight: '55vh',
      disableClose: true
    })
      .afterClosed()
      .pipe(take(1))
      .subscribe(outcome => {
        if (outcome) {
          LoggerService.logInfo(this.constructor.name, 'openNewBeneficiaryModal', { outcome });
          this.removeBeneficiary(beneficiary);
          this.addBeneficiary(outcome);
        }
      })
  }

  addBeneficiary(output): void {
    const outcome = output?.output;
    const additionalData = output?.additionalFormData;
    let newBeneficiary: IBeneficiaryObject;
    if (output?.existingSubject) {
      const isLegalSubject = outcome?.['ns2:naturaGiuridica'] !== ESubjectType.PF;
      const fieldsArray = isLegalSubject ? this.subjectPGFields : this.subjectPFFields;
      newBeneficiary = AthoraPayloadUtils.formatExistingBeneficiaryObject(outcome, fieldsArray, additionalData);
    } else {
      const isLegalSubject = outcome?.subjectTypeData?.specificBeneficiaryType === ESpecificBeneficiaryType.PERSONA_GIURIDICA;
      const isGenericBeneficiary = outcome?.subjectTypeData?.beneficiaryType === EBeneficiaryType.GENERIC_BENEFICIARY;
      const descBeneficiary = isGenericBeneficiary ? outcome?.subjectTypeData?.genericBeneficiary :
        (isLegalSubject ?
          outcome?.legalPersonData?.vatCode + ' - ' + outcome?.legalPersonData?.companyName :
          outcome?.individualData?.taxCode + ' - ' + outcome?.individualData?.name + ' ' + outcome?.individualData?.surname
        );
      newBeneficiary = {
          codeChoice: isGenericBeneficiary ? 'Beneficiario Testo Libero' : 'Altro beneficiario',
          type: outcome?.subjectTypeData?.beneficiaryTypeDesc,
          descBeneficiary: descBeneficiary,
          quote: isGenericBeneficiary ? '100' : (isLegalSubject ? outcome?.legalPersonData?.quote : outcome?.individualData?.quote),
          isNewBeneficiary: true,
          toBeDeleted: false
      }
      if (!isGenericBeneficiary) {
        if (!isLegalSubject) {
          newBeneficiary['beneficiaryTypeDesc'] = outcome?.individualData?.beneficiaryTypeDesc;
          newBeneficiary['taxCode'] = outcome?.individualData?.taxCode;
          newBeneficiary['birthDate'] = moment(outcome?.individualData.birthDate).format(DATE_FORMAT_FROM_BE);
          newBeneficiary['name'] = outcome?.individualData?.name;
          newBeneficiary['surname'] = outcome?.individualData?.surname;
          newBeneficiary['citizenship'] = outcome?.individualData?.citizenship;
          newBeneficiary['sex'] = outcome?.individualData?.sex;
          newBeneficiary['birthPlace'] = outcome?.birthData?.birthPlace;
          newBeneficiary['birthProvince'] = outcome?.birthData?.birthProvince;
          newBeneficiary['birthCountry'] = outcome?.birthData?.birthCountry;
          newBeneficiary['quote'] = outcome?.individualData?.quote;
          newBeneficiary['relationshipWithSubscriber'] = ERelationshipWithSubscriberDesc[outcome?.individualData?.relationshipWithSubscriber];
          newBeneficiary['iban'] = outcome?.individualData?.iban;
        } else {
          newBeneficiary['beneficiaryTypeDesc'] = outcome?.legalPersonData?.beneficiaryTypeDesc;
          newBeneficiary['companyName'] = outcome?.legalPersonData?.companyName;
          newBeneficiary['vatCode'] = outcome?.legalPersonData?.vatCode;
          newBeneficiary['shareCapital'] = outcome?.legalPersonData?.shareCapital;
          newBeneficiary['birthDate'] = moment(outcome?.legalPersonData.birthDate).format(DATE_FORMAT_FROM_BE);
          newBeneficiary['quote'] = outcome?.legalPersonData?.quote;
          newBeneficiary['relationshipWithSubscriber'] = ERelationshipWithSubscriberDesc[outcome?.legalPersonData?.relationshipWithSubscriber];
          newBeneficiary['iban'] = outcome?.legalPersonData?.iban;
        }
        newBeneficiary['residenceCountry'] = outcome?.residenceData?.country;
        newBeneficiary['residencePlace'] = outcome?.residenceData?.place;
        newBeneficiary['residenceProvince'] = outcome?.residenceData?.province;
        newBeneficiary['residencePostalCode'] = outcome?.residenceData?.postalCode;
        newBeneficiary['residenceToponym'] = outcome?.residenceData?.toponym;
        newBeneficiary['residenceAddress'] = outcome?.residenceData?.address;
        newBeneficiary['residenceAddressNumber'] = outcome?.residenceData?.addressNumber;
        newBeneficiary['isLegalSubject'] = isLegalSubject ? 'S' : 'N';
      }
    }
    LoggerService.logInfo(this.constructor.name, 'addBeneficiary', { newBeneficiary });
    this.beneficiariesList.push(newBeneficiary);
  }

  removeBeneficiary(beneficiary: IBeneficiaryObject): void {
    if (beneficiary) {
      if (beneficiary.isNewBeneficiary) {
        const index = this.beneficiariesList.findIndex(x => x.descBeneficiary === beneficiary.descBeneficiary);
        if (index !== -1) {
          this.beneficiariesList.splice(index, 1);
        }
      } else {
        beneficiary.toBeDeleted = true;
      }

      if (this.isSimor && this.questionnairesArray) {
        const fiscalCode: string = beneficiary.taxCode ?? beneficiary.vatCode;
        const index = this.questionnairesArray.findIndex(questionnaire => questionnaire.fiscalCode === fiscalCode);
        if (index !== -1) {
          this.questionnairesArray.splice(index, 1);
        }

        // Clean up liquidation subjects for this beneficiary
        this.cleanupLiquidationSubjectsForBeneficiary(fiscalCode);
      }
    }
  }

  getBeneficiaryQuote(beneficiary: IBeneficiaryObject): string {
    if (beneficiary) {
      return beneficiary.quote ? beneficiary.quote : '100';
    }
  }

  /**
   * Clean up all liquidation subjects (they are shared across beneficiaries in SIMOR)
   */
  cleanupLiquidationSubjectsForBeneficiary(fiscalCode: string): void {
    if (!this.newLiquidationSubjects) return;

    // Clear all liquidation subjects when beneficiary is removed
    // Since subjects use question codes (FATIT1, etc.) without fiscal code prefix,
    // we clear all to avoid stale data
    this.newLiquidationSubjects = {};
    this.newLiquidationSubjectsChange.emit({});
  }

  openBeneficiaryQuestionnaire(beneficiary: IBeneficiaryObject): void {
    const fiscalCode: string = beneficiary.taxCode ?? beneficiary.vatCode;
    const questionnaireForm: IFilledQuestionnaire = this.questionnairesArray.find(questionnaire => questionnaire.fiscalCode === fiscalCode);
    const questionnaire: IQuestionnaire = this.questionnaires.find(questionnaire => beneficiary?.isLegalSubject === 'S' ? questionnaire.questionnaireCode.includes('PG') : questionnaire.questionnaireCode.includes('PF'));
    const questionnaireObj = this.getQuestionnaireFormKeys(questionnaire);

    // Use existing liquidation subjects if available (e.g., from draft), otherwise start fresh
    const currentLiquidationSubjects = this.newLiquidationSubjects || {};

    // Store initial state to restore on cancel
    const initialLiquidationSubjects = { ...currentLiquidationSubjects };

    // Get the form that will be used in the modal
    const formToUse = !CommonUtils.isVoidObj(questionnaireForm) ? questionnaireForm.questionnaireForm : questionnaireObj?.questionnaireForm;

    // Store initial form values to restore on cancel
    const initialFormValues = formToUse ? { ...formToUse.value } : null;

    this._dialog.open(QuestionnaireModalComponent, {
      data: {
        // Pass original FormGroup instance to preserve reactive streams
        questionnaireForm: formToUse,
        formKeys: CommonUtils.assign(questionnaireObj?.keys),
        questionnaire: CommonUtils.assign(questionnaire),
        fiscalCode: fiscalCode,
        isReadOnly: this.isReadOnly,
        newLiquidationSubjects: currentLiquidationSubjects,
        onViewSubject: (key: IFormKey) => this.viewSubject.emit(key),
        onDeleteSubject: (key: IFormKey) => this.deleteSubject.emit(key),
        onNewLiquidationSubjectsChange: (subjects: Record<string, { rawFormOutcome: Record<string, any>; mappedOutcome: Record<string, any> }>) => {
          // Update the global liquidation subjects with new subjects from this beneficiary
          this.newLiquidationSubjects = { ...subjects };
          this.newLiquidationSubjectsChange.emit(this.newLiquidationSubjects);
        }
      },
      minWidth: '70vw',
      minHeight: '50vh',
      disableClose: true
    })
      .afterClosed()
      .pipe(take(1))
      .subscribe(outcome => {
        if (outcome) {
          LoggerService.logInfo(this.constructor.name, 'openBeneficiaryQuestionnaire', { outcome });
          const questionnaireIndex = this.questionnairesArray.findIndex(questionnaire => questionnaire.fiscalCode === fiscalCode);
          outcome.questionnaireCode = questionnaire.questionnaireCode;
          if (questionnaireIndex !== -1) {
            this.questionnairesArray[questionnaireIndex] = CommonUtils.assign(outcome);
          } else {
            this.questionnairesArray.push(outcome);
          }
        } else {
          // Modal was cancelled/closed without saving - restore initial state
          this.newLiquidationSubjects = initialLiquidationSubjects;
          this.newLiquidationSubjectsChange.emit(initialLiquidationSubjects);

          // Restore form values if they were modified (e.g., subject deleted)
          if (formToUse && initialFormValues) {
            formToUse.patchValue(initialFormValues);
          }
        }
      })
  }

  getQuestionnaireFormKeys(questionnaire: IQuestionnaire): {
    keys: IFormKey[],
    questionnaireForm: FormGroup
  } {
    let keys: IFormKey[] = [];
    let questionnaireForm: FormGroup = null;
    if (questionnaire && questionnaire.questions.length > 0) {
      questionnaire.questions.forEach(item => {
        const type = this.getAnswerType(item);
        const obj: IFormKey = {
          id: item.questionCode,
          controlName: item.questionCode,
          label: item.questionDescription,
          type: type,
          options: type === EFormType.SELECT ? item.answerValues.map(x => ({ key: x, value: x })) : [],
          readonly: item.isHidden,
          defaultValue: item.defaultAnswer,
          isInternal: item.isInternal,
        }
        keys.push(obj);
      });
      const template = keys.reduce((a, b) => {
        const value = b.defaultValue ?
          (b.type === EFormType.RADIO ? ERadioValue[b.defaultValue.toUpperCase()] : (b.defaultValue)) : null;
        a[b.controlName] = value;
        return a;
      }, {});
      const validators = keys.reduce((a, b) => {
        a[b.controlName] = [Validators.required];
        return a;
      }, {});
      LoggerService.logInfo(this.constructor.name, 'getFormKeys', { keys, template, validators });
      questionnaireForm = FormUtils.initFormGroupFromTemplate(template, validators);

    }
    return { keys: keys, questionnaireForm: questionnaireForm };
  }

  getAnswerType(question: IQuestion): EFormType {
    if (question.answerValues.length === 2 && question.answerValues[0].includes('true')) {
      return EFormType.RADIO;
    } else if (question.answerValues.length > 1) {
      return EFormType.SELECT;
    } else if (question.answerValues.length === 1 && question.answerValues[0].includes('text')) {
      return EFormType.INPUT;
    }
    return null;
  }

  retrieveCountries(): void {
    this._spinnerService.show();
    this._athoraService.retrieveCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          LoggerService.logInfo(this.constructor.name, 'retrieveCountries', { response });
          if (response) {
            this.countriesList = response;
          }
          this._spinnerService.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retrieveCountries', { error });
          this._alertService.openErrorMessage('Errore nel recupero della lista delle nazioni', 'retrieveCountries', error);
          this._spinnerService.hide();
        }
      });
  }

  showQuestionnaireIcon(beneficiary: IBeneficiaryObject): boolean {
    return this.isSimor && beneficiary.isNewBeneficiary;
  }

  isQuestionnaireCompiled(beneficiary: IBeneficiaryObject): boolean {
    const fiscalCode: string = beneficiary.taxCode ?? beneficiary.vatCode;
    return this.questionnairesArray && this.questionnairesArray.some(questionnaireObj => questionnaireObj.fiscalCode === fiscalCode);
  }

  canModifyBeneficiary(beneficiary: IBeneficiaryObject): boolean {
    return !CommonUtils.isVoid(beneficiary.toBeDeleted) &&!beneficiary.toBeDeleted && beneficiary.codeChoice !== 'Beneficiario Testo Libero';
  }

  get totalQuotas(): number {
    return this.beneficiariesList.reduce((acc, beneficiary) => acc + (beneficiary.toBeDeleted ? 0 : (beneficiary.quote ? parseFloat(beneficiary.quote?.replace(',', '.')) : 100)), 0);
  }

  get canAddNewBeneficiary(): boolean {
    return this.totalQuotas >= 100;
  }

  get maxQuotas(): boolean {
    return this.totalQuotas > 100;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
